import { API_BASE_URLS } from '@/constants/apiBaseUrl';
import { APP_ROUTES } from '@/routes/routes';
import { SegmentBuildContext, TutorialSegment, TutorialSegmentId, TutorialStep, TutorialStepData } from '@/types';

import { TUTORIAL_ANCHORS, dataTourSelector } from './anchors';
import { TUTORIAL_STEPS_DESCRIPTION } from './tutorialSteps';

const stepDefaults = {
  skipBeacon: true,
} as const;

const buildAiresSteps = ({ hasCompletedAiresRow }: SegmentBuildContext): TutorialStep[] => {
  const c = TUTORIAL_STEPS_DESCRIPTION.aires;
  const baseData: TutorialStepData = { segmentRoute: APP_ROUTES.aires, desiredMenuTab: null };
  const steps: TutorialStep[] = [
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.airesTab),
      title: c.activeTab.title,
      content: c.activeTab.body,
      data: { ...baseData },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.airesTable),
      title: c.uploadsTable.title,
      content: c.uploadsTable.body,
      data: { ...baseData },
    },
  ];

  if (hasCompletedAiresRow) {
    steps.push({
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.dataQualityCell),
      title: c.dataQualityCell.title,
      content: c.dataQualityCell.body,
      data: { ...baseData },
    });
  }

  steps.push({
    ...stepDefaults,
    target: dataTourSelector(TUTORIAL_ANCHORS.uploadDataBtn),
    title: c.uploadDataBtn.title,
    content: c.uploadDataBtn.body,
    data: { ...baseData },
  });

  return steps;
};

const buildLoansSteps = ({ counts, totals }: SegmentBuildContext): TutorialStep[] => {
  const c = TUTORIAL_STEPS_DESCRIPTION.loans;
  const route = APP_ROUTES.loansMapping;
  const closedMenu: TutorialStepData = { segmentRoute: route, desiredMenuTab: null };
  const openMenu: TutorialStepData = { segmentRoute: route, desiredMenuTab: TutorialSegmentId.loans };
  const bulkSelect: TutorialStepData = { ...closedMenu, desiredBulkSelectTab: TutorialSegmentId.loans };

  const steps: TutorialStep[] = [
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.loansTab),
      title: c.whatWhy.title,
      content: c.whatWhy.body,
      data: { ...closedMenu },
    },
  ];

  if (counts.loans > 0) {
    steps.push({
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.loansUnmappedCounter),
      title: c.unmappedCounter.title,
      content: c.unmappedCounter.body,
      data: { ...closedMenu },
    });
  }

  steps.push({
    ...stepDefaults,
    target: dataTourSelector(TUTORIAL_ANCHORS.mappingEditBtn),
    title: c.editBtn.title,
    content: c.editBtn.body,
    data: { ...closedMenu },
  });

  if (totals.loans > 0) {
    steps.push(
      {
        ...stepDefaults,
        target: dataTourSelector(TUTORIAL_ANCHORS.mappingSelectAllCheckbox),
        title: c.checkboxes.title,
        content: c.checkboxes.body,
        data: { ...bulkSelect },
      },
      {
        ...stepDefaults,
        target: dataTourSelector(TUTORIAL_ANCHORS.bulkUnmapBtn),
        title: c.bulkUnmap.title,
        content: c.bulkUnmap.body,
        placement: 'top',
        data: { ...bulkSelect },
      },
      {
        ...stepDefaults,
        target: dataTourSelector(TUTORIAL_ANCHORS.bulkAssignBtn),
        title: c.bulkAssign.title,
        content: c.bulkAssign.body,
        placement: 'top',
        data: { ...bulkSelect },
      },
    );
  }

  steps.push(
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.moreActionsBtn),
      title: 'More actions',
      content: 'Open this menu to access the loan-mapping shortcuts. The tour will step through each one.',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionAddNewProduct),
      title: c.moreActions.addNewProduct.title,
      content: c.moreActions.addNewProduct.body,
      placement: 'right',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionDownloadTemplate),
      title: c.moreActions.downloadTemplate.title,
      content: c.moreActions.downloadTemplate.body,
      placement: 'right',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionUploadMapFile),
      title: c.moreActions.uploadMapFile.title,
      content: c.moreActions.uploadMapFile.body,
      placement: 'right',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionOpenMappingSpec),
      title: c.moreActions.openSpec.title,
      content: c.moreActions.openSpec.body,
      placement: 'right',
      data: { ...openMenu },
    },
  );

  return steps;
};

const buildSharesSteps = (): TutorialStep[] => {
  const c = TUTORIAL_STEPS_DESCRIPTION.shares;
  const route = APP_ROUTES.sharesMapping;
  const closedMenu: TutorialStepData = { segmentRoute: route, desiredMenuTab: null };

  return [
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.sharesTab),
      title: c.whatWhy.title,
      content: c.whatWhy.body,
      data: { ...closedMenu },
    },
  ];
};

const buildPfcSteps = ({ counts, totals }: SegmentBuildContext): TutorialStep[] => {
  const c = TUTORIAL_STEPS_DESCRIPTION.pfc;
  const route = APP_ROUTES.codeMapping;
  const closedMenu: TutorialStepData = { segmentRoute: route, desiredMenuTab: null };
  const openMenu: TutorialStepData = { segmentRoute: route, desiredMenuTab: TutorialSegmentId.pfc };

  const steps: TutorialStep[] = [
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.pfcTab),
      title: c.whatWhy.title,
      content: c.whatWhy.body,
      data: { ...closedMenu },
    },
  ];

  if (counts.pfc > 0) {
    steps.push({
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.pfcUnmappedCounter),
      title: c.unmappedCounter.title,
      content: c.unmappedCounter.body,
      data: { ...closedMenu },
    });
  }

  steps.push({
    ...stepDefaults,
    target: dataTourSelector(TUTORIAL_ANCHORS.mappingEditBtn),
    title: c.editBtn.title,
    content: c.editBtn.body,
    data: { ...closedMenu },
  });

  if (totals.pfc > 0) {
    steps.push({
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.pfcRowEditBtn),
      title: c.singleEdit.title,
      content: c.singleEdit.body,
      data: { ...closedMenu },
    });
  }

  steps.push(
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.moreActionsBtn),
      title: 'More actions',
      content: 'Open this menu to access the PFC-mapping shortcuts. The tour will step through each one.',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionDownloadTemplate),
      title: c.moreActions.downloadTemplate.title,
      content: c.moreActions.downloadTemplate.body,
      placement: 'right',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionUploadMapFile),
      title: c.moreActions.uploadMapFile.title,
      content: c.moreActions.uploadMapFile.body,
      placement: 'right',
      data: { ...openMenu },
    },
    {
      ...stepDefaults,
      target: dataTourSelector(TUTORIAL_ANCHORS.actionOpenMappingSpec),
      title: c.moreActions.openSpec.title,
      content: c.moreActions.openSpec.body,
      placement: 'right',
      data: { ...openMenu },
    },
  );

  return steps;
};

export const TUTORIAL_SEGMENTS: TutorialSegment[] = [
  {
    id: TutorialSegmentId.aires,
    route: APP_ROUTES.aires,
    buildSteps: buildAiresSteps,
  },
  {
    id: TutorialSegmentId.loans,
    route: APP_ROUTES.loansMapping,
    buildSteps: buildLoansSteps,
  },
  {
    id: TutorialSegmentId.shares,
    route: APP_ROUTES.sharesMapping,
    buildSteps: buildSharesSteps,
  },
  {
    id: TutorialSegmentId.pfc,
    route: APP_ROUTES.codeMapping,
    buildSteps: buildPfcSteps,
  },
];

export const TUTORIAL_SEGMENT_LABELS: Record<TutorialSegmentId, string> = {
  [TutorialSegmentId.aires]: TUTORIAL_STEPS_DESCRIPTION.aires.label,
  [TutorialSegmentId.loans]: TUTORIAL_STEPS_DESCRIPTION.loans.label,
  [TutorialSegmentId.shares]: TUTORIAL_STEPS_DESCRIPTION.shares.label,
  [TutorialSegmentId.pfc]: TUTORIAL_STEPS_DESCRIPTION.pfc.label,
  [TutorialSegmentId.finish]: 'Finish',
};

export const MY_DATA_CLIENT_URL = API_BASE_URLS.MY_DATA_CLIENT_URL;
